// ─── macysAgent.js ────────────────────────────────────────────────────────────
// Agent for Macy's (via Anthropic mock).
// Outputs { item, brand, price, rating, inStock, store } + Macy's extras.
// ─────────────────────────────────────────────────────────────────────────────

const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate a Macy's API search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 45.00,
      "original_price": 72.00,
      "rating": 4.3,
      "review_count": 1240,
      "product_id": "123456",
      "shade": null,
      "size": "1.7 oz",
      "seller_type": "Macys",
      "is_sale": true,
      "sale_label": "30% off",
      "has_star_rewards": true,
      "star_rewards_label": "Extra 15% with Star Rewards",
      "is_same_day": false,
      "is_store_pickup": true,
      "store_count": 500,
      "shipping_free": true,
      "shipping_cost": 0,
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL Macy's product names, brands, and realistic prices for "${query}"
- Macy's carries: beauty (Clinique, Estee Lauder, Lancome, MAC, Bobbi Brown, DKNY), fashion (Calvin Klein, Ralph Lauren, Tommy Hilfiger, Michael Kors), home (Hotel Collection, Charter Club), jewelry, shoes
- product_id: 6-digit number
- shade: include for beauty/makeup products, null otherwise
- size: include for beauty products, null for fashion/home
- is_sale: true for 2 items (Macy's is known for frequent sales)
- sale_label: e.g. "40% off", "Extra 25% off", "Buy 2 Get 1 Free"
- has_star_rewards: true for all (Macy's Star Rewards loyalty program)
- star_rewards_label: e.g. "Extra 15% with Star Rewards card"
- original_price: always higher than price for sale items
- is_store_pickup: true for all (500+ Macy's stores)
- is_same_day: true for 1 item in major metro areas
- shipping_free: true if price >= 25 (Macy's free shipping threshold)
- Macy's skews mid-to-premium — prices typically $25–$150 for beauty
- Price at low / mid / premium tiers for "${query}"`;
}

async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });
  if (!response.ok) throw new Error(`Macy's agent API error: ${response.status}`);
  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';
  const first   = rawText.indexOf('{');
  const last    = rawText.lastIndexOf('}');
  if (first === -1) throw new Error("Macy's agent: no JSON found");
  return JSON.parse(rawText.slice(first, last + 1));
}

function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100) : null;

  return {
    item:               raw.title           || 'Unknown Product',
    brand:              raw.brand           || 'Unknown',
    price,
    rating:             parseFloat(raw.rating) || 4.0,
    inStock:            raw.availability    !== 'Out of Stock',
    store:              store.id,

    // Macy's-specific extras
    product_id:         raw.product_id      || null,
    shade:              raw.shade           || null,
    size:               raw.size            || null,
    original_price:     originalPrice,
    discount_pct:       discountPct,
    review_count:       parseInt(raw.review_count) || 0,
    seller_type:        'Macys',
    is_sale:            raw.is_sale         === true,
    sale_label:         raw.sale_label      || null,
    has_star_rewards:   raw.has_star_rewards === true,
    star_rewards_label: raw.star_rewards_label || null,
    is_same_day:        raw.is_same_day     === true,
    is_store_pickup:    raw.is_store_pickup === true,
    store_count:        parseInt(raw.store_count) || 0,
    shipping_free:      raw.shipping_free   === true,
    shipping_cost:      parseFloat(raw.shipping_cost) || 0,
    total_cost:         +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:        'macys',
  };
}

export async function runMacysAgent(store, query) {
  const prompt = buildPrompt(query);
  const parsed = await callAPI(prompt);
  const items  = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

export default runMacysAgent;
